import * as React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { NavItem } from '../NavItem';
import { MoreHorizontal } from '../icons';
import { COLORS, BREAKPOINTS } from '../styles';
import { NavOption } from '../types';
import ExtendedMainMenu from './ExtendedMainMenu';

const NavContainer = styled.div<{ open: boolean }>`
  display: none;
  justify-content: center;
  align-items: center;

  button {
    border-width: 0;
    cursor: pointer;
    background-color: ${props => (props.open ? `${COLORS.primary_100} !important` : 'transparent')};
    ${props => (props.open ? `box-shadow: none` : '')};
  }

  ${BREAKPOINTS.MEDIUM} {
    display: flex;
  }
`;

const NavButton = styled.button`
  background: ${COLORS.white};
  border: 0;
`;

const StyledButton = styled.button`
  &:focus,
  &:active {
    background-color: ${COLORS.primary_100};
    outline: none;

    &::after {
      background-color: ${COLORS.white};
    }
  }

  &:hover {
    background-color: ${COLORS.primary_20};
    color: ${COLORS.primary_600};
  }
  padding: 0;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  outline: none;
  svg {
    color: ${COLORS.neutral_600};
    &:focus,
    &:active {
      color: ${COLORS.black};
    }
  }
`;

interface ExtendedMenuWrapperProps {
  floatRight: boolean;
}

const ExtendedMenuWrapper = styled.div<ExtendedMenuWrapperProps>`
  display: flex;
  margin-left: ${props => (props.floatRight ? 'auto' : 'unset')};

  position: absolute;
  flex-direction: column;
  left: 290px;
  top: 22px;

  height: 0;
  width: 0;

  &:focus-within,
  &:hover,
  &.open {
    background: ${COLORS.white};
  }
`;

const ExtendedSection = styled.div`
  position: relative;
`;

type Props = {
  navigationOptions: NavOption[];
  rightSideRef?: React.RefObject<HTMLDivElement> | null | undefined;
};

const MainMenu = ({ navigationOptions, rightSideRef }: Props): React.ReactElement<Props> => {
  const [showExtendedMenu, setShowExtendedMenu] = React.useState(false);
  const extendedButtonRef = React.useRef<any>(null);
  const extendedMenuRef = React.useRef<any>(null);
  const mainMenuRef: React.RefObject<HTMLDivElement> | null | undefined = React.useRef(null);
  const [numberOfItemsAllowed, setNumberOfItemsAllowed] = React.useState<number>(1);

  const widthOfItem = 150;
  const recalculateNumberOfItemsAllowed = () => {
    if (rightSideRef?.current && mainMenuRef?.current) {
      const number = Math.floor((rightSideRef?.current?.offsetLeft - mainMenuRef?.current?.offsetLeft) / widthOfItem);
      if (number !== numberOfItemsAllowed) {
        setNumberOfItemsAllowed(number);
      }
    }
  };

  const handleClickExtendedMenuAction = () => {
    setShowExtendedMenu(false);
  };

  const handleClickOutside = (e: any) => {
    if (extendedMenuRef?.current && !extendedMenuRef?.current?.contains(e.target)) {
      if (showExtendedMenu) {
        setShowExtendedMenu(false);
      }
    }
  };
  React.useEffect(() => {
    recalculateNumberOfItemsAllowed();
    // Bind the event listener
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('resize', recalculateNumberOfItemsAllowed);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', recalculateNumberOfItemsAllowed);
    };
  });

  const preventFocusOnClick = (e: any) => {
    e.preventDefault();
  };

  return (
    <NavContainer className="GlobalNavigationMainMenu" ref={mainMenuRef} open={showExtendedMenu}>
      {navigationOptions?.map((no: NavOption, index: number) =>
        index > numberOfItemsAllowed - 2 ? null : no?.disabled ? (
          <NavItem
            as={NavButton}
            exact={no?.exact}
            className="disabled"
            key={no?.label}
            onMouseDown={preventFocusOnClick}
            onClick={() => {
              if (no?.onClick) {
                no?.onClick();
              }
            }}>
            {no?.label}
          </NavItem>
        ) : (
          <NavItem
            as={NavLink}
            exact={no?.exact}
            to={no?.to || ''}
            key={no?.label}
            onMouseDown={preventFocusOnClick}
            onClick={() => {
              if (no?.onClick) {
                no?.onClick();
              }
            }}>
            {no?.label}
          </NavItem>
        ),
      )}
      {navigationOptions.length + 1 > numberOfItemsAllowed && (
        <ExtendedSection>
          <div style={{ padding: '8px' }}>
            <StyledButton
              ref={extendedButtonRef}
              onClick={() => {
                setShowExtendedMenu(true);
              }}>
              <MoreHorizontal size="24px" />
            </StyledButton>
          </div>
          <ExtendedMenuWrapper ref={extendedMenuRef} className={showExtendedMenu ? 'open' : ''} floatRight={false}>
            <ExtendedMainMenu clickMenuAction={handleClickExtendedMenuAction} navigationOptions={navigationOptions.slice(numberOfItemsAllowed - 1)} />
          </ExtendedMenuWrapper>
        </ExtendedSection>
      )}
    </NavContainer>
  );
};

export default MainMenu;
